import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const LOGO_URL = "/logo-brand.png";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      // New dealers (from Stripe signup) land on /first-run for the wizard.
      const nextPath = res.user && res.user.onboarding_completed === false ? "/first-run" : "/dashboard";
      navigate(nextPath);
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="grid md:grid-cols-[minmax(0,420px)_1fr] flex-1">
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex items-center gap-3 mb-2">
            <img
              src={LOGO_URL}
              alt="Decline Drive"
              className="w-24 h-24 object-contain shrink-0"
            />
            <div>
              <div className="font-display font-black text-xl leading-none text-navy">
                Decline Drive
              </div>
              <div className="gold-divider mt-2 mb-2" />
              <div className="font-mono-label text-foreground/70 text-[0.6rem]">
                Ayers Enterprises
              </div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tighter">Sign in</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Recover declined service revenue with automated follow-ups.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="font-mono-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dealership.com"
                required
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-mono-label">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full group"
            data-testid="login-submit-btn"
          >
            {busy ? "Signing in..." : "Sign in"}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
          </Button>
          <p className="text-xs text-muted-foreground">
            New dealership? <Link to="/signup" className="text-navy underline font-medium" data-testid="login-signup-link">Start your 60-day guarantee →</Link>
          </p>
          <p className="text-[0.65rem] text-muted-foreground pt-4 border-t border-border mt-4" data-testid="copyright-notice">
            © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. All rights reserved.
          </p>
        </form>
      </div>
      <div
        className="hidden md:block relative bg-cover bg-center min-h-[720px]"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(6,15,45,0.35) 0%, rgba(6,15,45,0.10) 40%, rgba(6,15,45,0.80) 100%), url("https://images.pexels.com/photos/5864155/pexels-photo-5864155.jpeg?auto=compress&cs=tinysrgb&w=2000&h=1400&dpr=2")',
        }}
        data-testid="login-hero-image"
      >
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-base opacity-90 max-w-md drop-shadow-md">
            Turn every declined repair into a scheduled appointment with automated,
            timed SMS offers — no advisor lifts a finger.
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
